import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../lib/firebase';
import type {
  ListMissingOriginalAmountsResponse,
  OriginalAmountEntry,
  SetLoanOriginalAmountsResponse,
} from './types';

/**
 * The missing-original worklist (listMissingOriginalAmounts) and the save call
 * (setLoanOriginalAmounts). Same RBAC scope as the radar, enforced server side.
 */
export function useMissingOriginalAmounts(companyId: string | undefined, page: number) {
  const [data, setData] = useState<ListMissingOriginalAmountsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable<{ companyId: string; page: number }, ListMissingOriginalAmountsResponse>(
        functions,
        'listMissingOriginalAmounts',
      );
      setData((await fn({ companyId, page })).data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load loans missing an original amount');
    } finally {
      setLoading(false);
    }
  }, [companyId, page]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const save = useCallback(
    async (entries: OriginalAmountEntry[], source: 'manual' | 'upload'): Promise<SetLoanOriginalAmountsResponse> => {
      if (!companyId) throw new Error('No company');
      const fn = httpsCallable<
        { companyId: string; entries: OriginalAmountEntry[]; source: 'manual' | 'upload' },
        SetLoanOriginalAmountsResponse
      >(functions, 'setLoanOriginalAmounts');
      return (await fn({ companyId, entries, source })).data;
    },
    [companyId],
  );

  return { data, loading, error, reload, save };
}
